/**
 * Note: This Wizard component will throw an error if at least 2 children are not passed to it.
 */

import { createMuiTheme, MuiThemeProvider, Step, StepButton, Stepper } from '@material-ui/core';
import React, { useState } from 'react';

import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';

interface IWizardProps {
  children: React.ReactNodeArray;
  stepNames?: string[];
  canSkipPages?: boolean;
  removeFalseChildren?: boolean;
}

export interface IWizardStepProps {
  onNext?: () => void;
  onBack?: () => void;
}

const wizardTheme = createMuiTheme({
  overrides: {
    MuiStepIcon: {
      root: {
        color: '#e0e0e0',
        '&$active': {
          color: '#e1e4fe',
        },
      },
      text: {
        fill: '#747474',
        '&$active': {
          color: '#4952fe',
        },
      },
    },
    MuiStepLabel: {
      label: {
        '&$active': {
          color: '#4952fe',
        },
      },
    },
    MuiSwitch: {
      colorPrimary: {
        '&$checked + $track': {
          backgroundColor: getColorTheme('multiSwitchBackground'), // Slide button background
        },
      },
      colorSecondary: {
        '&$checked + $track': {
          backgroundColor: getColorTheme('multiSwitchBackground'), // Slide button background
        },
        '&$checked': {
          color: getColorTheme('multiSwitchHead') + ' !important', // Slide button head color
        },
      },
    },
  },
});

const Wizard = ({ children, stepNames, canSkipPages, removeFalseChildren }: IWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepReached, setStepReached] = useState(0);
  children = removeFalseChildren ? children.filter((child) => child !== false) : children;

  const handleNext = () => {
    if (currentStep === children.length - 1) {
      return;
    }
    setCurrentStep(currentStep + 1);

    if (currentStep + 1 > stepReached) {
      setStepReached(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStep = (step: number) => {
    if (step < 0 || step > children.length - 1) {
      return;
    }

    // The following logic will allow the user to click on the steps
    // if they have reached that step before. Otherwise, they will
    // be forced to click on the next button.
    if (!canSkipPages && step > stepReached) {
      return;
    }

    setCurrentStep(step);
  };

  return (
    <MuiThemeProvider theme={wizardTheme}>
      <div
        style={{
          padding: '20px',
        }}
      >
        <Stepper
          nonLinear
          activeStep={currentStep}
          style={{
            backgroundColor: 'transparent',
          }}
        >
          {stepNames &&
            stepNames.map((label, index) => (
              <Step key={index} completed={false}>
                <StepButton onClick={() => handleStep(index)}>{label}</StepButton>
              </Step>
            ))}
        </Stepper>

        <div
          style={{
            paddingLeft: '35px',
          }}
        >
          {children.map((child, index) => {
            // Render the correct page
            if (index === currentStep && React.isValidElement(child)) {
              // Send the handleNext and handleBack functions to the child
              return React.cloneElement(child, {
                onNext: handleNext,
                onBack: handleBack,
              });
            }

            return null;
          })}
        </div>
      </div>
    </MuiThemeProvider>
  );
};

export default Wizard;
